import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isGuest: boolean;
  loading: boolean;
    role: string;
  roleLoading: boolean;
  signInWithMagicLink: (email: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<{ error: string | null }>;
}

const GUEST_USER = {
  id: 'guest',
  email: 'guest@dipps.ca',
  app_metadata: {},
  user_metadata: { display_name: 'Guest' },
  aud: 'authenticated',
  created_at: new Date().toISOString(),
} as unknown as User;

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isGuest: true,
  loading: true,
    role: 'senior',
  roleLoading: true,
  signInWithMagicLink: async () => ({ error: null }),
  signOut: async () => {},
  deleteAccount: async () => ({ error: null }),
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isGuest, setIsGuest] = useState(true);
  const [loading, setLoading] = useState(true);
    const [role, setRole] = useState<string>('senior');
  const [roleLoading, setRoleLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) {
      setUser(GUEST_USER);
      setIsGuest(true);
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (s?.user) {
        setSession(s);
        setUser(s.user);
        setIsGuest(false);
                  // Fetch role from profile
                  supabase.from('profiles').select('role').eq('id', s.user.id).single().then(({ data }) => {
                                setRole(data?.role || 'senior');
        setRoleLoading(false);
                  });
      } else {
        setUser(GUEST_USER);
        setIsGuest(true);
      }
        setRoleLoading(false);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (s?.user) {
        setSession(s);
        setUser(s.user);
        setIsGuest(false);
                  // Fetch role from profile
                  supabase.from('profiles').select('role').eq('id', s.user.id).single().then(({ data }) => {
                                setRole(data?.role || 'senior');
        setRoleLoading(false);
                  });
      } else {
        setSession(null);
        setUser(GUEST_USER);
        setIsGuest(true);
        setRoleLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithMagicLink = useCallback(async (email: string) => {
    if (!supabase) return { error: 'Supabase not configured' };
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/#/` },
    });
    return { error: error?.message || null };
  }, []);

  const signOut = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    setSession(null);
    setUser(GUEST_USER);
    setIsGuest(true);
  }, []);

  const deleteAccount = useCallback(async () => {
    if (!supabase) return { error: 'Supabase not configured' };
    try {
      const { data: { session: cs } } = await supabase.auth.getSession();
      if (!cs) return { error: 'Not signed in' };
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${cs.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        return { error: data.error || 'Failed to delete account' };
      }
      await supabase.auth.signOut();
      setSession(null);
      setUser(GUEST_USER);
      setIsGuest(true);
      return { error: null };
    } catch (err: any) {
      return { error: err.message || 'Failed to delete account' };
    }
  }, []);

  const value = useMemo(() => ({
    user,
    session,
    isGuest,
    loading,
    role,
    roleLoading,
    signInWithMagicLink,
    signOut,
    deleteAccount
  }), [user, session, isGuest, loading, role, roleLoading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
